import { Accordion, AccordionDetails, AccordionGroup, AccordionSummary, Stack } from '@mui/joy';
import { CaretDown } from '@phosphor-icons/react';
import { ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, CheckboxInput, Input, SlidingPanel } from 'core/components';
import { Loader } from 'core/elements';
import { trimFilterText } from 'core/utils';
import { ITssDashboardFilters } from 'reports/components';
import { useGetExternalLocationRegionsAutocomplete } from 'reports/hooks';
import { Color, Direction, Size, Variant } from 'theme/enums';

interface IProps {
  isOpen: boolean;
  onChange: (filters: ITssDashboardFilters) => void;
  onClose: () => void;
  value: ITssDashboardFilters;
}

export const FilterPanel = ({ isOpen, onClose, value, onChange }: IProps) => {
  const [filters, setFilters] = useState<ITssDashboardFilters>(value);

  const { t } = useTranslation();

  const { externalLocationRegions: theatres, isLoading: isLoadingTheatres } =
    useGetExternalLocationRegionsAutocomplete({});

  useEffect(() => {
    if (isOpen) {
      setFilters(value);
    }
  }, [isOpen, value]);

  const onTheatreChange = (theatre: string, checked: boolean) => {
    setFilters((prev) => {
      const currentTheatres = prev.theatreName || [];
      const updatedTheatres = checked
        ? [...currentTheatres, theatre]
        : currentTheatres.filter((t) => t !== theatre);

      return {
        ...prev,
        theatreName: updatedTheatres,
      };
    });
  };

  const onOwnerChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setFilters((prev) => ({
      ...prev,
      creatorName: value || undefined,
    }));
  };

  const onClientChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setFilters((prev) => ({
      ...prev,
      customerCompanyName: value || undefined,
    }));
  };

  const onView = () => {
    onChange({
      ...filters,
      creatorName: trimFilterText(filters.creatorName) || undefined,
      customerCompanyName: trimFilterText(filters.customerCompanyName) || undefined,
    });
    onClose();
  };

  const onResetAll = () => {
    const clearedFilters: ITssDashboardFilters = {};
    setFilters(clearedFilters);
    onChange(clearedFilters);
    onClose();
  };

  return (
    <SlidingPanel
      isOpen={isOpen}
      onClosePanel={onClose}
      title={t('headerFilters')}
      size={Size.MD}
      buttons={
        <Stack direction={Direction.ROW} gap={1}>
          <Button text={t('buttonLabelView')} onClick={onView} />
          <Button
            text={t('buttonLabelResetAll')}
            variant={Variant.PLAIN}
            color={Color.NEUTRAL}
            onClick={onResetAll}
          />
        </Stack>
      }
    >
      <AccordionGroup>
        <Stack gap={2}>
          <Accordion defaultExpanded>
            <AccordionSummary indicator={<CaretDown />}>{t('labelTheatre')}</AccordionSummary>
            <AccordionDetails>
              <Stack gap={1}>
                {isLoadingTheatres && <Loader />}
                {!isLoadingTheatres &&
                  theatres.map((theatre: string) => (
                    <CheckboxInput
                      key={theatre}
                      label={theatre}
                      value={filters.theatreName?.includes(theatre) ?? false}
                      onChange={(e) => onTheatreChange(theatre, e.target.checked)}
                    />
                  ))}
              </Stack>
            </AccordionDetails>
          </Accordion>

          <Accordion defaultExpanded={!!filters.creatorName}>
            <AccordionSummary indicator={<CaretDown />}>{t('labelOwner')}</AccordionSummary>
            <AccordionDetails>
              <Input
                placeholder={t('labelOwner')}
                value={filters.creatorName || ''}
                onChange={onOwnerChange}
              />
            </AccordionDetails>
          </Accordion>

          <Accordion defaultExpanded={!!filters.customerCompanyName}>
            <AccordionSummary indicator={<CaretDown />}>{t('labelCustomer')}</AccordionSummary>
            <AccordionDetails>
              <Input
                placeholder={t('labelCustomer')}
                value={filters.customerCompanyName || ''}
                onChange={onClientChange}
              />
            </AccordionDetails>
          </Accordion>
        </Stack>
      </AccordionGroup>
    </SlidingPanel>
  );
};
